import React from 'react';
import { Check, ShieldCheck, Sparkles, Users, Zap, AlertCircle, Loader2 } from 'lucide-react';

const GURU_IMG = '/gleam-guru-favicon.png?v=4';

export const Brand = () => (
  <div className="flex items-center gap-2.5" data-testid="intake-brand">
    <img src={GURU_IMG} alt="Gleam Guru" className="w-9 h-9 rounded-lg" />
    <div className="leading-none">
      <p className="font-heading text-sm tracking-wide text-zinc-900">GLEAM GURU</p>
      <p className="text-[9px] tracking-[0.2em] text-zinc-500 uppercase">Cleaners</p>
    </div>
  </div>
);

export const IntakeFrame = ({ children, footer, progress }) => (
  <div className="min-h-screen bg-white flex flex-col" data-testid="intake-page">
    <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-zinc-100">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between"><Brand /></div>
      {progress}
    </header>
    <main className="flex-1 w-full max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">{children}</main>
    {footer && (
      <footer className="sticky bottom-0 z-20 bg-white/95 backdrop-blur border-t border-zinc-100">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">{footer}</div>
      </footer>
    )}
  </div>
);

export const Progress = ({ stages, current }) => (
  <nav aria-label="Progress" className="max-w-2xl mx-auto px-4 sm:px-6 pb-3">
    <ol className="flex items-center gap-1.5 sm:gap-2">
      {stages.map((s, i) => {
        const done = i < current, active = i === current;
        return (
          <li key={s} className="flex-1 min-w-0" aria-current={active ? 'step' : undefined} data-testid={`stage-${i}`}>
            <div className={`h-1.5 rounded-full transition-colors duration-300 ${done || active ? 'bg-[#F56F05]' : 'bg-zinc-200'}`} />
            <p className={`mt-1.5 text-[10px] sm:text-xs font-semibold truncate ${active ? 'text-zinc-900' : done ? 'text-[#F56F05]' : 'text-zinc-400'}`}>
              {done && <Check className="inline w-3 h-3 mr-0.5 -mt-0.5" aria-label="completed" />}{s}
            </p>
          </li>
        );
      })}
    </ol>
  </nav>
);

export const PrimaryButton = ({ children, onClick, disabled, loading, testId, type = 'button' }) => (
  <button type={type} onClick={onClick} disabled={disabled || loading} data-testid={testId}
    className="w-full h-13 min-h-[52px] rounded-full bg-[#F56F05] text-white font-bold text-base hover:bg-[#e06505] active:scale-[0.99] transition-[background-color,transform] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#F56F05]">
    {loading && <Loader2 className="w-4 h-4 animate-spin" />}{children}
  </button>
);

export const GhostButton = ({ children, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="h-[52px] px-5 rounded-full border border-zinc-200 text-zinc-700 font-semibold hover:bg-zinc-50 active:scale-[0.99] transition-[background-color,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F56F05]/60">
    {children}
  </button>
);

export const WelcomeScreen = ({ firstName, onStart }) => (
  <IntakeFrame footer={<PrimaryButton onClick={onStart} testId="intake-start-btn">Let's Get Started</PrimaryButton>}>
    <div className="pt-6 sm:pt-10">
      <img src={GURU_IMG} alt="" className="w-20 h-20 rounded-2xl mb-8" />
      <h1 className="font-heading text-4xl sm:text-5xl text-zinc-900 leading-[1.05]">Tell Us About<br />Your Space</h1>
      <p className="text-base text-zinc-600 mt-5 max-w-md">{firstName ? `Hi ${firstName}! ` : ''}Help your Guru understand your space and what matters most to you. It only takes a few minutes.</p>
      <ul className="mt-8 space-y-3">
        {[[Sparkles, 'Personalized clean for your space'], [Users, 'Better prepared Gurus'], [Zap, 'Faster, more accurate estimate']].map(([Icon, t]) => (
          <li key={t} className="flex items-center gap-3 text-sm sm:text-base text-zinc-800">
            <span className="w-9 h-9 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-[#F56F05]" /></span>{t}
          </li>
        ))}
      </ul>
      <p className="mt-10 inline-flex items-center gap-1.5 text-xs text-zinc-500"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Your information is secure and private.</p>
    </div>
  </IntakeFrame>
);

export const SuccessScreen = () => (
  <IntakeFrame footer={<PrimaryButton onClick={() => { window.close(); window.location.assign('/'); }} testId="intake-close-btn">Close This Page</PrimaryButton>}>
    <div className="pt-8 text-center" data-testid="intake-success">
      <img src={GURU_IMG} alt="Gleam Guru" className="w-24 h-24 rounded-3xl mx-auto mb-6" />
      <h1 className="font-heading text-3xl sm:text-4xl text-zinc-900">You're all set!</h1>
      <p className="text-base text-zinc-600 mt-3 max-w-md mx-auto">Your Guru has the details. We'll review your space and reach out with the next step.</p>
      <div className="mt-10 text-left rounded-2xl border border-zinc-200 p-5">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">What happens next?</p>
        <ol className="space-y-3">
          {['We review your space details', 'Our team may reach out with a few follow-up questions', "We'll provide the appropriate next steps"].map((t, i) => (
            <li key={t} className="flex items-start gap-3 text-sm text-zinc-800"><span className="w-6 h-6 rounded-full bg-[#F56F05] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>{t}</li>
          ))}
        </ol>
      </div>
    </div>
  </IntakeFrame>
);

export const ErrorScreen = ({ title, message }) => (
  <IntakeFrame>
    <div className="pt-10 text-center" data-testid="intake-error">
      <span className="w-14 h-14 rounded-full bg-red-50 border border-red-100 inline-flex items-center justify-center mb-5"><AlertCircle className="w-6 h-6 text-red-500" /></span>
      <h1 className="font-heading text-2xl text-zinc-900">{title}</h1>
      <p className="text-sm text-zinc-600 mt-2 max-w-sm mx-auto">{message}</p>
      <p className="text-xs text-zinc-400 mt-8">Questions? Call or text Gleam Guru at (971) 715-0335.</p>
    </div>
  </IntakeFrame>
);
